// ModulerPage — the candidate learning hub (#74). One aggregated fetch
// (/api/my/learning) drives a structured, single-language page:
//   • Fortsätt-hero    — one-click resume of the active course.
//   • Mina program     — each assigned program is its own panel (progress ring +
//                        an ordered module checklist). A journey, not a bag.
//   • Fler moduler      — the optional self-serve catalog, same row language.
//   • Genomförda moduler — collapsed history, expanded on demand.
// Every course renders through the shared CourseRow, on Nexilink tokens + palette.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, GraduationCap, BookOpen, Lock, ChevronRight, ChevronDown, PlayCircle } from 'lucide-react';
import { learningApi } from '@/lib/api/learningApi';
import type { MyLearningResponse, LearningContinue } from '@/lib/models/learning';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { LoadingState, EmptyState } from '@/components/custom-ui/empty-state';
import { ExplainerCard } from '@/components/custom-ui/ExplainerCard';
import { ProgramPanel } from '@/components/modules/ProgramPanel';
import { CourseRow } from '@/components/modules/CourseRow';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';
import { cn } from '@/lib/utils';

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
    <div className="mb-3">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
);

const ModulerPage: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<MyLearningResponse | null>(null);
    const [showHistory, setShowHistory] = useState(false);

    useEffect(() => {
        let cancelled = false;
        learningApi.getMyLearning().then((r) => { if (!cancelled) setData(r); });
        return () => { cancelled = true; };
    }, []);

    if (!data) return <LoadingState message="Laddar moduler..." />;

    const openModule = (id: string) => navigate(`/dashboard/modules/${id}`);
    // Seatbelt: Go marshals a nil slice as `null`, so a list the API promises can
    // still arrive as null. The backend now always sends [] (learning_service.go),
    // but a single missing initialisation upstream should not white-screen the
    // page a candidate reaches from the sidebar.
    const cont = data.continue;
    const onboarding_programs = data.onboarding_programs ?? [];
    const platform_modules = data.platform_modules ?? [];
    const history = data.history ?? [];
    const intro = data.intro;
    const isEmpty =
        !cont && onboarding_programs.length === 0 && platform_modules.length === 0 && history.length === 0;

    return (
        <div className="space-y-8">
            <ExplainerCard id="candidate-courses" icon={Lightbulb} tone="amber" title="Så funkar moduler">
                <p className="text-sm text-gray-600 mt-0.5">
                    Här samlas din <strong>onboarding</strong> — moduler din arbetsgivare tilldelat dig — och <strong>fler moduler</strong> du kan ta på plattformen. Slutför dem i din egen takt; dina framsteg sparas automatiskt.
                </p>
            </ExplainerCard>

            {cont && <ContinueHero cont={cont} onClick={() => openModule(cont.module_id)} />}

            {onboarding_programs.length > 0 && (
                <section className="space-y-4">
                    <SectionHeader title="Mina program" subtitle="Moduler du blivit tilldelad av din arbetsgivare" />
                    {onboarding_programs.map((program) => (
                        <ProgramPanel key={program.program_id} program={program} onOpenModule={openModule} />
                    ))}
                </section>
            )}

            <section>
                <SectionHeader
                    title={onboarding_programs.length > 0 ? 'Fler moduler' : 'Moduler från Nexilink'}
                    subtitle="Frivillig introduktion och utbildning på plattformen"
                />
                {!intro.intro_completed && intro.intro_module_id && (
                    <div className="p-4 mb-3 rounded-2xl bg-amber-50 border border-amber-200 flex items-center gap-3">
                        <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <div className="min-w-0">
                            <p className="font-semibold text-gray-900 text-sm">Introduktionsmodul krävs</p>
                            <p className="text-sm text-gray-600">Slutför introduktionsmodulen för att låsa upp alla moduler.</p>
                        </div>
                        <button
                            onClick={() => openModule(intro.intro_module_id!)}
                            className="ml-auto px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl text-sm transition-colors whitespace-nowrap"
                        >
                            Starta intro
                        </button>
                    </div>
                )}
                {platform_modules.length > 0 ? (
                    <div className={cn(NEXILINK_CARD_WHITE_ELEVATED, 'p-2 divide-y divide-gray-50')}>
                        {platform_modules.map((m) => (
                            <CourseRow
                                key={m.id}
                                title={m.name}
                                description={m.description}
                                organisationName={m.organisation_name}
                                organisationLogoUrl={m.organisation_logo_url}
                                estimatedDuration={m.estimated_duration}
                                totalSteps={m.total_steps}
                                status={m.status}
                                locked={m.locked}
                                percentage={m.percentage}
                                isMandatory={m.is_mandatory}
                                onClick={() => openModule(m.id)}
                            />
                        ))}
                    </div>
                ) : (
                    onboarding_programs.length > 0 && (
                        <p className="text-sm text-gray-400">Inga fler moduler tillgängliga just nu.</p>
                    )
                )}
            </section>

            {history.length > 0 && (
                <section>
                    <button
                        type="button"
                        onClick={() => setShowHistory((v) => !v)}
                        className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-[#1A4D4F] transition-colors"
                    >
                        <span>Genomförda moduler</span>
                        <span className="text-sm font-medium text-gray-400">({history.length})</span>
                        <ChevronDown className={cn('w-5 h-5 text-gray-400 transition-transform', showHistory && 'rotate-180')} />
                    </button>
                    {showHistory && (
                        <div className={cn(NEXILINK_CARD_WHITE_ELEVATED, 'mt-3 p-2 divide-y divide-gray-50')}>
                            {history.map((r) => (
                                <CourseRow
                                    key={r.id}
                                    title={r.module_name}
                                    description={[r.program_name, r.organisation_name].filter(Boolean).join(' · ') || undefined}
                                    organisationName={r.organisation_name}
                                    organisationLogoUrl={r.organisation_logo_url}
                                    status="completed"
                                    completedAt={r.completed_at}
                                    // A finished module was unreachable: no onClick
                                    // here, and the viewer opens completed modules on
                                    // the celebration screen. So a candidate could
                                    // never re-read the content or see what they
                                    // wrote, while their employer could.
                                    onClick={() => openModule(r.module_id)}
                                />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {isEmpty && (
                <EmptyState icon={BookOpen} title="Inga moduler ännu" description="När du tilldelas en onboarding eller nya moduler dyker de upp här." />
            )}
        </div>
    );
};

// ── Continue hero ─────────────────────────────────────────────────
const ContinueHero: React.FC<{ cont: LearningContinue; onClick: () => void }> = ({ cont, onClick }) => {
    const thumb = resolveMediaUrl(cont.thumbnail_url);
    return (
        <button
            type="button"
            onClick={onClick}
            className="group w-full text-left rounded-2xl overflow-hidden border border-[#1A4D4F]/20 bg-gradient-to-br from-[#1A4D4F] to-[#2d6e70] text-white flex flex-col sm:flex-row transition-all hover:shadow-lg"
        >
            <div className="relative w-full sm:w-44 h-28 sm:h-auto flex-shrink-0 bg-white/10">
                {thumb ? (
                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center"><GraduationCap className="w-9 h-9 text-white/50" /></div>
                )}
            </div>
            <div className="flex-1 p-5 flex flex-col justify-center min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#A4C3B2] mb-1">Fortsätt där du slutade</p>
                <h3 className="text-lg font-bold truncate">{cont.module_name}</h3>
                {cont.program_name && <p className="text-sm text-white/70 truncate">{cont.program_name}</p>}
                <div className="mt-3 max-w-md">
                    <div className="flex justify-between text-xs text-white/80 mb-1">
                        <span>{cont.total_steps > 0 ? `Steg ${Math.min(cont.current_step_order + 1, cont.total_steps)} av ${cont.total_steps}` : 'Pågående'}</span>
                        <span>{cont.percentage}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                        <div className="h-full bg-[#A4C3B2] rounded-full transition-all" style={{ width: `${cont.percentage}%` }} />
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center p-5 sm:pr-6">
                <span className="flex items-center gap-2 bg-white text-[#1A4D4F] font-semibold px-5 py-2.5 rounded-xl group-hover:gap-3 transition-all whitespace-nowrap">
                    <PlayCircle className="w-5 h-5" /> Fortsätt <ChevronRight className="w-4 h-4" />
                </span>
            </div>
        </button>
    );
};

export default ModulerPage;
